import { useEffect, useState } from 'react'

import type { CareLogCardArea } from './-care-log-list-card'
import { AreaPolygonThumb } from '@/components/growers/area-map/area-polygon-thumb'
import { GrowerAreaMap } from '@/components/growers/area-map/grower-area-map'
import { normalizeGeo } from '@/components/growers/area-map/use-area-polygon'
import {
    GoogleMapsProvider,
    hasGoogleMapsKey,
} from '@/components/maps/google-maps-provider'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { m } from '@/lib/paraglide/messages'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    areas: CareLogCardArea[]
}

/**
 * Phóng to vùng trồng của một nhật ký trên ảnh vệ tinh. Tái sử dụng nguyên
 * `GrowerAreaMap` ở chế độ chỉ xem (readOnly) — cùng một bản đồ với màn hộ
 * trồng chè, nên hộ dân nhìn thấy đúng hình thửa họ đã vẽ. Nhật ký nhiều vùng
 * thì có nút chọn vùng thay vì mở nhiều bản đồ chồng nhau.
 */
export function CareLogAreaMapDialog({ open, onOpenChange, areas }: Props) {
    const [index, setIndex] = useState(0)
    // Mở bản đồ của nhật ký khác thì phải về vùng đầu, không giữ tab của
    // nhật ký trước (số vùng có thể ít hơn → index cũ trỏ ra ngoài mảng).
    useEffect(() => {
        if (open) setIndex(0)
    }, [open])

    const area = areas[index] ?? areas[0]
    if (!area) return null
    const geo = normalizeGeo(area.paths, area.markers)

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{m.care_log_parcels()}</DialogTitle>
                </DialogHeader>

                {areas.length > 1 && (
                    <div className="flex flex-wrap gap-1.5">
                        {areas.map((a, i) => (
                            <Button
                                key={a.id}
                                type="button"
                                size="sm"
                                variant={i === index ? 'secondary' : 'outline'}
                                onClick={() => setIndex(i)}
                            >
                                {a.name}
                            </Button>
                        ))}
                    </div>
                )}

                {geo.paths.length === 0 ? (
                    <div className="flex h-40 items-center justify-center rounded-md border bg-muted text-sm text-muted-foreground">
                        {m.care_log_area_no_geo()}
                    </div>
                ) : !hasGoogleMapsKey() ? (
                    /* Chưa cấu hình key Google Maps: vẫn vẽ hình thửa bằng SVG
                       để hộ dân nhận ra vùng, thay vì một ô báo lỗi trống. */
                    <AreaPolygonThumb
                        paths={area.paths}
                        markers={area.markers}
                        className="h-80 w-full rounded-md border"
                    />
                ) : (
                    <GoogleMapsProvider>
                        <GrowerAreaMap
                            value={{ paths: area.paths, markers: area.markers }}
                            onChange={() => {}}
                            readOnly
                            height={320}
                            fitKey={area.id}
                            neighbors={{ excludeAreaIds: [area.id] }}
                        />
                    </GoogleMapsProvider>
                )}

                <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-medium">{area.name}</span>
                    {area.acreage ? (
                        <span className="tabular-nums text-muted-foreground">
                            {m.care_log_area_acreage({
                                acreage: area.acreage,
                            })}
                        </span>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    )
}
